import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, MessageCircle, Check, Clock, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { serviceSlugs, serviceImages, serviceIcons, CONTACT } from "@/i18n/content";
import { Reveal, MaskedLines } from "@/components/site/Reveal";

const EASE = [0.16, 1, 0.3, 1];

export default function ServiceDetail() {
  const { t, lang } = useLang();
  const { slug } = useParams();
  const idx = serviceSlugs.indexOf(slug);

  if (idx === -1) return <Navigate to="/" replace />;

  const total = serviceSlugs.length;
  const item = t.services.items[idx];
  const L = t.serviceDetail;
  const Icon = serviceIcons[idx];
  const img = serviceImages[idx];

  const prevIdx = (idx - 1 + total) % total;
  const nextIdx = (idx + 1) % total;
  const prevItem = t.services.items[prevIdx];
  const nextItem = t.services.items[nextIdx];

  const msg =
    lang === "en"
      ? `Hello, I'd like to book a ${item.name} consultation with Nakshtra Vidhya.`
      : `नमस्ते, मुझे नक्षत्र विद्या से ${item.name} का परामर्श बुक करना है।`;
  const waLink = `${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`;

  return (
    <div data-testid="service-detail-page" className="pt-28 md:pt-36">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Reveal y={20}>
          <Link
            to="/#services"
            data-testid="service-back-link"
            className="nav-link inline-flex items-center gap-2 text-sm text-ink-soft"
          >
            <ArrowLeft size={16} strokeWidth={1.75} /> {L.back}
          </Link>
        </Reveal>

        {/* Hero */}
        <div className="mt-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <Icon size={26} strokeWidth={1.3} className="text-terracotta" />
              <span className="overline !text-sage">{item.tag}</span>
            </div>
            <h1
              data-testid="service-detail-title"
              className="heading-serif mt-5 text-5xl leading-[0.95] tracking-tight text-forest sm:text-6xl lg:text-7xl"
            >
              <MaskedLines lines={[item.name]} trigger="load" />
            </h1>
            <Reveal delay={0.15}>
              <p className="body-text mt-7 max-w-xl text-base md:text-lg">{item.long}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-ink-soft">
                <Clock size={15} strokeWidth={1.6} className="text-terracotta" />
                <span>{L.duration}:</span>
                <span className="text-ink">{item.duration}</span>
              </div>
            </Reveal>

            {item.highlights && (
              <Reveal delay={0.32}>
                <div className="mt-5 flex flex-wrap gap-2.5" data-testid="service-highlights">
                  {item.highlights.map((h, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 rounded-full bg-forest/10 px-4 py-2 text-sm text-forest"
                    >
                      <Star size={13} strokeWidth={1.75} className="text-terracotta" />
                      {h}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: EASE, delay: 0.3 }}
              className="hero-arch mx-auto aspect-[4/5] w-full max-w-sm overflow-hidden"
            >
              <img src={img} alt={item.name} className="warm-img h-full w-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* Expect + Bring */}
        <div className="mt-20 grid grid-cols-1 gap-12 border-t border-line pt-16 md:mt-28 lg:grid-cols-2 lg:gap-20">
          <div data-testid="service-expect-list">
            <Reveal>
              <p className="overline mb-8">{L.expect}</p>
            </Reveal>
            <ul className="flex flex-col">
              {item.expect.map((e, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="grid grid-cols-[auto_1fr] gap-5 border-b border-line py-5">
                    <span className="heading-serif text-2xl leading-none text-terracotta">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="body-text text-ink">{e}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div data-testid="service-bring-list">
            <Reveal>
              <p className="overline mb-8">{L.bring}</p>
            </Reveal>
            <ul className="flex flex-col gap-4">
              {item.bring.map((b, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex items-start gap-4">
                    <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sage/15">
                      <Check size={15} strokeWidth={2} className="text-sage" />
                    </span>
                    <span className="body-text text-ink">{b}</span>
                  </li>
                </Reveal>
              ))}
            </ul>

            <Reveal delay={0.2}>
              <p className="body-text mt-10 border-l-2 border-terracotta/40 pl-5 text-sm italic">
                {L.note}
              </p>
            </Reveal>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-wrap items-center gap-4 md:mt-20">
          <a href={CONTACT.tel} data-testid="service-call-btn" className="btn-primary">
            <Phone size={17} strokeWidth={1.75} /> {L.cta}
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="service-whatsapp-btn"
            className="btn-ghost"
          >
            <MessageCircle size={17} strokeWidth={1.75} /> {L.ask}
          </a>
        </div>

        {/* Prev / Next */}
        <div className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line md:mt-28">
          <Link
            to={`/service/${serviceSlugs[prevIdx]}`}
            data-testid="service-prev-link"
            className="group bg-bg p-7 transition-colors hover:bg-surface md:p-9"
          >
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink-soft">
              <ArrowLeft size={14} strokeWidth={1.75} className="transition-transform group-hover:-translate-x-1" />
              {L.prev}
            </span>
            <span className="heading-serif mt-3 block text-2xl text-forest md:text-3xl">
              {prevItem.name}
            </span>
          </Link>
          <Link
            to={`/service/${serviceSlugs[nextIdx]}`}
            data-testid="service-next-link"
            className="group bg-bg p-7 text-right transition-colors hover:bg-surface md:p-9"
          >
            <span className="flex items-center justify-end gap-2 text-xs uppercase tracking-[0.18em] text-ink-soft">
              {L.next}
              <ArrowRight size={14} strokeWidth={1.75} className="transition-transform group-hover:translate-x-1" />
            </span>
            <span className="heading-serif mt-3 block text-2xl text-forest md:text-3xl">
              {nextItem.name}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}
